import { motion, AnimatePresence } from "framer-motion";
import { useState, useMemo, useEffect } from "react";
import { TransactionItemSkeleton } from "@/components/LoadingSkeleton";
import { EmptyState } from "@/components/EmptyState";
import { useData } from "@/contexts/DataContext";
import {
  Receipt,
  IndianRupee,
  Filter,
  Share2,
  Check,
  Loader2,
  Trash2,
  ArrowUpRight,
  ArrowDownRight,
  ChevronRight,
  Printer,
  Bluetooth,
  Usb,
  MessageSquare,
  Send,
  X
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Logo } from "@/components/Logo";
import { useToast } from "@/hooks/use-toast";
import { ReportDialog } from "@/components/ReportDialog";
import { useAuth } from "@/hooks/use-auth";

// --- PRINTER & FIREBASE IMPORTS ---
import { getPublicInvoice, deletePublicInvoice } from "@/lib/firebase";
import { saveCustomerPhone } from "@/lib/googleSheetsService";
import {
  connectUSBPrinter,
  sendToUSBPrinter,
  getCachedUSBPrinter
} from "@/lib/usbPrinter";
import {
  connectBTPrinter,
  sendToBTPrinter,
  getCachedBTPrinter
} from "@/lib/btPrinter";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

// ─── ESC/POS LOGIC ───
const ESC = 0x1b;
const GS = 0x1d;
let LINE_WIDTH = 32;

function escposInit(): number[] { return [ESC, 0x40]; }
function escposCenterOn(): number[] { return [ESC, 0x61, 1]; }
function escposCenterOff(): number[] { return [ESC, 0x61, 0]; }
function escposBoldOn(): number[] { return [ESC, 0x45, 1]; }
function escposBoldOff(): number[] { return [ESC, 0x45, 0]; }
function escposDoubleHeight(): number[] { return [ESC, 0x21, 0x10]; }
function escposNormal(): number[] { return [ESC, 0x21, 0x00]; }
function escposCut(): number[] { return [GS, 0x56, 0x00]; }
function escposFeed(lines: number): number[] { return [ESC, 0x64, lines]; }
function textToBytes(text: string): number[] {
  const bytes: number[] = [];
  for (let i = 0; i < text.length; i++) {
    const code = text.charCodeAt(i);
    bytes.push(code > 127 ? 63 : code);
  }
  return bytes;
}
function escposLine(text: string): number[] { return [...textToBytes(text), 0x0a]; }
function escposDashedLine(): number[] { return escposLine("-".repeat(LINE_WIDTH)); }
function escposTwoCol(left: string, right: string): number[] {
  const space = LINE_WIDTH - left.length - right.length;
  if (space > 0) return escposLine(left + " ".repeat(space) + right);
  const maxLeft = LINE_WIDTH - right.length - 1;
  return escposLine(left.slice(0, maxLeft) + " " + right);
}
function wrapText(text: string, maxWidth: number): string[] {
  const lines: string[] = [];
  let remaining = text;
  while (remaining.length > maxWidth) {
    let breakAt = remaining.lastIndexOf(" ", maxWidth);
    if (breakAt <= 0) breakAt = maxWidth;
    lines.push(remaining.slice(0, breakAt));
    remaining = remaining.slice(breakAt).trimStart();
  }
  if (remaining.length > 0) lines.push(remaining);
  return lines;
}
function escposFourCol(c1: string, c2: string, c3: string, c4: string): number[] {
  const w1 = 10, w2 = 8, w3 = 7, w4 = 7;
  const s1 = c1.slice(0, w1).padEnd(w1);
  const s2 = c2.slice(0, w2).padStart(w2);
  const s3 = c3.slice(0, w3).padStart(w3);
  const s4 = c4.slice(0, w4).padStart(w4);
  return escposLine(s1 + s2 + s3 + s4);
}

function escposQrCode(url: string): number[] {
  const urlBytes = textToBytes(url);
  const storeLen = urlBytes.length + 3;
  const pL = storeLen % 256;
  const pH = Math.floor(storeLen / 256);

  return [
    // Model 2
    GS, 0x28, 0x6b, 0x04, 0x00, 0x31, 0x41, 0x32, 0x00,
    // Size (1-16, larger is bigger)
    GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x43, 0x06,
    // Error correction level (M)
    GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x45, 0x31,
    // Store data
    GS, 0x28, 0x6b, pL, pH, 0x31, 0x50, 0x30, ...urlBytes,
    // Print
    GS, 0x28, 0x6b, 0x03, 0x00, 0x31, 0x51, 0x30
  ];
}

async function imageToEscPosRaster(imageDataUrl: string, maxWidth: number): Promise<number[]> {
  return new Promise((resolve, reject) => {
    if (!imageDataUrl) return reject(new Error("No URL"));
    const img = new Image();
    img.crossOrigin = "anonymous";
    const timeoutId = setTimeout(() => { img.src = ""; reject(new Error("Timeout")); }, 2500);
    img.onload = () => {
      clearTimeout(timeoutId);
      const scale = Math.min(1, maxWidth / img.width);
      let w = Math.floor(img.width * scale);
      let h = Math.floor(img.height * scale);
      w = Math.ceil(w / 8) * 8;
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      if (!ctx) return reject(new Error("No ctx"));
      ctx.fillStyle = "white"; ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      const pixels = ctx.getImageData(0, 0, w, h).data;
      const bytesPerRow = w / 8;
      const data: number[] = [];
      data.push(GS, 0x76, 0x30, 0, bytesPerRow & 0xff, (bytesPerRow >> 8) & 0xff, h & 0xff, (h >> 8) & 0xff);
      for (let y = 0; y < h; y++) {
        for (let byteX = 0; byteX < bytesPerRow; byteX++) {
          let byte = 0;
          for (let bit = 0; bit < 8; bit++) {
            const idx = (y * w + (byteX * 8 + bit)) * 4;
            if (0.299 * pixels[idx] + 0.587 * pixels[idx + 1] + 0.114 * pixels[idx + 2] < 128) byte |= 1 << (7 - bit);
          }
          data.push(byte);
        }
      }
      resolve(data);
    };
    img.onerror = () => { clearTimeout(timeoutId); reject(new Error("Error")); };
    img.src = imageDataUrl;
  });
}

// Module-scope group processor — same logic as in-component processGroup,
// re-usable by UltimateTransactions reprint flow.
export function processTransactionGroup(group: any[], products: any[] = []) {
  const first = group[0];
  const isSale = first.type === "sale";
  const orderId = String(first.fullOrderId || first.id.split("_")[0] || "");
  const isGst = orderId.startsWith("INV-") || first.isGstBill;

  const filteredSaleItems = group.filter((t) => !t.productName.toLowerCase().includes("discount"));
  const discountItem = group.find((t) => t.productName.toLowerCase().includes("discount"));
  const discountAmount = discountItem
    ? Math.abs(Number(discountItem.total) || (discountItem.quantity * discountItem.rate))
    : 0;
  const invoiceGrossTotal = filteredSaleItems.reduce(
    (sum, item) => sum + Number(item.quantity * item.rate || 0),
    0
  );
  const discountRatio =
    invoiceGrossTotal > 0 ? Math.max(0, invoiceGrossTotal - discountAmount) / invoiceGrossTotal : 1;

  let uiTotalGst = 0;
  const hsnSummary: Record<string, { taxable: number; cgst: number; sgst: number }> = {};

  const discountedItems = filteredSaleItems.map((item) => {
    const product = products.find((p) => p.id === item.productId) as any;
    const hsn = item.hsn || product?.hsn || "N/A";
    const rawItemTotal = Number(item.quantity * item.rate || 0);
    const discountedTotalBase = rawItemTotal * discountRatio;
    const discountedRateBase = item.rate * discountRatio;

    let cgst = Number(item.cgst) || 0;
    let sgst = Number(item.sgst) || 0;
    const tTotal = Number(item.total || 0);
    let taxable = discountedTotalBase;
    let displayTotal = discountedTotalBase;
    let displayRate = discountedRateBase;

    if (item.taxableAmount !== undefined) {
      taxable = Number(item.taxableAmount);
      if (Math.abs(tTotal - (taxable + cgst + sgst)) < 0.1) {
        displayTotal = tTotal;
        displayRate = tTotal / Number(item.quantity || 1);
      } else {
        displayTotal = taxable + cgst + sgst;
        displayRate = displayTotal / Number(item.quantity || 1);
      }
    } else if (cgst > 0 || sgst > 0) {
      const expectedExclusiveTotal = discountedTotalBase + cgst + sgst;
      if (Math.abs(tTotal - expectedExclusiveTotal) < 0.1) {
        taxable = discountedTotalBase;
        displayTotal = tTotal;
        displayRate = tTotal / Number(item.quantity || 1);
      } else {
        taxable = discountedTotalBase - (cgst + sgst);
        displayTotal = discountedTotalBase;
        displayRate = discountedRateBase;
      }
    }

    uiTotalGst += cgst + sgst;
    if (!hsnSummary[hsn]) hsnSummary[hsn] = { taxable: 0, cgst: 0, sgst: 0 };
    hsnSummary[hsn].taxable += taxable;
    hsnSummary[hsn].cgst += cgst;
    hsnSummary[hsn].sgst += sgst;

    return {
      ...item,
      rate: displayRate,
      total: displayTotal,
      visualRate: displayRate,
      visualTotal: displayTotal,
      cgst,
      sgst,
      hsn,
    };
  });

  const netPayable = isSale
    ? discountedItems.reduce((sum, t) => sum + t.total, 0)
    : group.reduce((sum: number, t: any) => sum + (Number(t.total) || 0), 0);

  return { isSale, isGst, orderId, discountAmount, invoiceGrossTotal, netPayable, uiTotalGst, discountedItems, hsnSummary };
}

// ─── UNIFIED PRINT ENGINE ───
export async function buildReceiptEscPos(opts: any): Promise<Uint8Array> {
  const isBasic = opts.layout === 'basic';
  LINE_WIDTH = opts.paperWidth === "80" ? 48 : 32;
  const d: number[] = [];
  d.push(...escposInit());
  d.push(...escposCenterOn());

  if (!isBasic && opts.businessLogo && opts.printLogo !== false) {
    try {
      const rasterData = await imageToEscPosRaster(opts.businessLogo, 200);
      d.push(...rasterData);
      d.push(0x0a);
    } catch (e) {
      console.error("Failed to add logo to receipt:", e);
    }
  }

  d.push(...escposDoubleHeight());
  d.push(...escposBoldOn());
  for (const line of wrapText(opts.businessName.toUpperCase(), LINE_WIDTH)) {
    d.push(...escposLine(line));
  }
  d.push(...escposNormal());
  d.push(...escposBoldOff());

  if (opts.businessAddress) {
    for (const line of wrapText(opts.businessAddress, LINE_WIDTH)) {
      d.push(...escposLine(line));
    }
  }

  if (!isBasic) {
    if (opts.businessPhone) d.push(...escposLine(opts.businessPhone));
    if (opts.businessEmail) {
      for (const line of wrapText(opts.businessEmail, LINE_WIDTH)) d.push(...escposLine(line));
    }
    if (opts.businessWebsite) {
      for (const line of wrapText(opts.businessWebsite, LINE_WIDTH)) d.push(...escposLine(line));
    }
    if (opts.isGstBill && opts.gstin) {
      d.push(...escposBoldOn());
      d.push(...escposLine(`GSTIN: ${opts.gstin}`));
      d.push(...escposBoldOff());
    }
    if (opts.isGstBill && opts.cin) d.push(...escposLine(`CIN: ${opts.cin}`));
    if (opts.isGstBill && opts.fssai) d.push(...escposLine(`FSSAI: ${opts.fssai}`));
  }

  d.push(...escposCenterOff());
  d.push(...escposDashedLine());

  d.push(
    ...escposTwoCol(
      `DATE: ${opts.invoiceDate.toLocaleDateString("en-GB")}`,
      `TIME: ${opts.invoiceDate.toLocaleTimeString("en-US", { hour12: false })}`,
    ),
  );

  const displayId = opts.orderId.startsWith("INV-")
    ? `INV #: ${opts.orderId}`
    : `${isBasic ? 'BILL #:' : 'ORDER #:'} ${opts.orderId.slice(-8).toUpperCase()}`;
  d.push(...escposLine(displayId));

  d.push(...escposDashedLine());

  if (!isBasic) {
    d.push(...escposBoldOn());
    d.push(...escposTwoCol("Qty/Item", "Total"));
    d.push(...escposBoldOff());
    d.push(...escposDashedLine());
  }

  for (const item of opts.items) {
    if (isBasic) {
      const leftText = `${item.qty}x ${item.name}`;
      d.push(...escposTwoCol(leftText, item.total.toFixed(0)));
    } else {
      const name = item.name.toUpperCase();
      d.push(...escposBoldOn());
      for (const line of wrapText(name, LINE_WIDTH)) {
        d.push(...escposLine(line));
      }
      d.push(...escposBoldOff());
      const qtyRate = `${item.qty} x ${item.rate.toFixed(2)}`;
      const totalStr = `Rs.${item.total.toFixed(2)}`;
      d.push(...escposTwoCol(qtyRate, totalStr));
      if (opts.isGstBill && item.hsn) {
        d.push(...escposLine(`  HSN: ${item.hsn}`));
      }
    }
  }

  d.push(...escposDashedLine());

  if (isBasic) {
    let totalGstAmount = 0;
    if (opts.isGstBill && opts.items) {
      for (const item of opts.items) {
        totalGstAmount += (item.cgst || 0) + (item.sgst || 0);
      }
    }

    if (opts.discount > 0) {
      d.push(...escposTwoCol("Total with Discount", `Rs. ${opts.netPayable.toFixed(2)}`));
      d.push(...escposTwoCol("Discount Applied", `Rs. ${opts.discount.toFixed(2)}`));
    } else if (totalGstAmount > 0) {
      d.push(...escposTwoCol("Total", `Rs. ${opts.netPayable.toFixed(2)}`));
    }

    if (totalGstAmount > 0) {
      d.push(...escposTwoCol("Total GST", `Rs. ${totalGstAmount.toFixed(2)}`));
    }

    d.push(...escposBoldOn(), ...escposTwoCol("NET PAYABLE", `Rs. ${opts.netPayable.toFixed(2)}`), ...escposBoldOff());
  } else {
    d.push(...escposBoldOn());
    if (opts.discount > 0) {
      d.push(...escposTwoCol("TOTAL WITH DISCOUNT", `Rs.${opts.netPayable.toFixed(2)}`));
      d.push(...escposTwoCol("YOUR SAVINGS TODAY", `Rs.${opts.discount.toFixed(2)}`));
    } else {
      d.push(...escposTwoCol("TOTAL", `Rs.${opts.netPayable.toFixed(2)}`));
    }

    if (opts.isGstBill && opts.hsnSummary && Object.keys(opts.hsnSummary).length > 0) {
      d.push(...escposDashedLine());
      d.push(...escposLine("GST SUMMARY"));
      d.push(...escposBoldOff());
      d.push(...escposFourCol("HSN", "Taxable", "CGST", "SGST"));
      d.push(...escposDashedLine());
      for (const [hsn, v] of Object.entries(opts.hsnSummary)) {
        d.push(...escposFourCol(hsn, (v as any).taxable.toFixed(2), (v as any).cgst.toFixed(2), (v as any).sgst.toFixed(2)));
      }
      d.push(...escposBoldOn());
    }
    d.push(...escposDashedLine());
    d.push(...escposDoubleHeight());
    d.push(...escposTwoCol("NET PAYABLE", `Rs.${opts.netPayable.toFixed(2)}`));
    d.push(...escposNormal());
    d.push(...escposBoldOff());
  }

  d.push(...escposDashedLine());

  if (!isBasic && opts.invoiceTerms) {
    d.push(...escposBoldOn());
    d.push(...escposLine("TERMS & CONDITIONS:"));
    d.push(...escposBoldOff());
    for (const line of wrapText(opts.invoiceTerms, LINE_WIDTH)) {
      d.push(...escposLine(line));
    }
    d.push(0x0a);
  }

  d.push(...escposCenterOn());

  if (!isBasic && opts.printQrCode && opts.invoiceUrl) {
    d.push(...escposQrCode(opts.invoiceUrl));
    d.push(0x0a);
    d.push(...escposLine("SCAN TO DOWNLOAD INVOICE"));
    d.push(0x0a);
  }

  if (!isBasic && opts.invoiceFooter && opts.invoiceFooter.trim().length > 0) {
    const footerSegments = opts.invoiceFooter.split('\n');
    for (const segment of footerSegments) {
      for (const line of wrapText(segment, LINE_WIDTH)) {
        d.push(...escposLine(line));
      }
    }
  } else if (!isBasic) {
    d.push(...escposLine("THANK YOU FOR SHOPPING!"));
    d.push(...escposLine("Visit again soon"));
  }

  d.push(0x0a);
  if (opts.businessVerified) {
    // keep in sync with the DAILYHISAAB VERIFIED badge
    d.push(...escposBoldOn());
    d.push(...escposLine("**** DAILYHISAAB VERIFIED ****"));
    d.push(...escposBoldOff());
  }
  d.push(...escposLine("Generated by DailyHisaab"));
  d.push(...escposLine("WWW.DAILYHISAAB.COM"));
  d.push(...escposCenterOff());
  d.push(...escposFeed(4));
  d.push(...escposCut());
  return new Uint8Array(d);
}

export default function Transactions() {
  const {
    transactions, isLoading, deleteTransaction, convertToGstBill,
    businessProfile, businessLogo, dashboardStats, userMode,
    printerPaperWidth, printerPrintLogo, invoiceFooter, invoiceTerms,
    publishInvoice, spreadsheetId, invoiceLayout, products, printQrCode,
    businessVerified
  } = useData();
  const { user } = useAuth();
  const { toast } = useToast();

  const [pageSize, setPageSize] = useState(25);
  const [showTodaySales, setShowTodaySales] = useState(false);
  const [showPrinterConnect, setShowPrinterConnect] = useState(false);
  const [typeFilter, setTypeFilter] = useState<FilterType>("all");
  const [dateFilter, setDateFilter] = useState<DateFilter>("all");

  const [actionTransactionGroup, setActionTransactionGroup] = useState<any[] | null>(null);
  const [deleteConfirmGroup, setDeleteConfirmGroup] = useState<any[] | null>(null);
  const [gstConfirmGroup, setGstConfirmGroup] = useState<any[] | null>(null);
  const [showPhoneInput, setShowPhoneInput] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState("");
  const [btStatus, setBtStatus] = useState<"idle" | "connecting" | "printing" | "success" | "error">("idle");
  const [usbStatus, setUsbStatus] = useState<"idle" | "connecting" | "printing" | "success" | "error">("idle");

  const [shortUrl, setShortUrl] = useState<string | null>(null);

  // --- NEW PROCESS GROUP HELPER (Reads explicit DB Taxable Amount) ---
  const processGroup = (group: any[]) => {
    const first = group[0];
    const isSale = first.type === "sale";
    const orderId = String(first.fullOrderId || first.id.split("_")[0] || "");
    const isGst = orderId.startsWith("INV-") || first.isGstBill;

    const filteredSaleItems = group.filter(t => !t.productName.toLowerCase().includes("discount"));
    const discountItem = group.find(t => t.productName.toLowerCase().includes("discount"));
    // Match the popup calculation perfectly
    const discountAmount = discountItem ? Math.abs(Number(discountItem.total) || (discountItem.quantity * discountItem.rate)) : 0;

    const invoiceGrossTotal = filteredSaleItems.reduce((sum, item) => sum + Number(item.quantity * item.rate || 0), 0);
    const discountRatio = invoiceGrossTotal > 0 ? Math.max(0, invoiceGrossTotal - discountAmount) / invoiceGrossTotal : 1;

    let uiTotalGst = 0;
    const hsnSummary: Record<string, { taxable: number; cgst: number; sgst: number }> = {};

    const discountedItems = filteredSaleItems.map((item) => {
      const product = products.find((p) => p.id === item.productId) as any;
      const hsn = item.hsn || product?.hsn || "N/A";
      const rawItemTotal = Number(item.quantity * item.rate || 0);

      const discountedTotalBase = rawItemTotal * discountRatio;
      const discountedRateBase = item.rate * discountRatio;

      let cgst = Number(item.cgst) || 0;
      let sgst = Number(item.sgst) || 0;
      let tTotal = Number(item.total || 0);

      let taxable = discountedTotalBase;
      let displayTotal = discountedTotalBase;
      let displayRate = discountedRateBase;

      // EXPLICIT DB VALUE CHECK
      if (item.taxableAmount !== undefined) {
        taxable = Number(item.taxableAmount);

        if (Math.abs(tTotal - (taxable + cgst + sgst)) < 0.1) {
          displayTotal = tTotal;
          displayRate = tTotal / Number(item.quantity || 1);
        } else {
          // FIX: For Converted GST bills, tTotal is the old raw base. 
          // We must explicitly add the newly attached CGST & SGST to display the correct Inclusive line item.
          displayTotal = taxable + cgst + sgst;
          displayRate = displayTotal / Number(item.quantity || 1);
        }
      } else {
        // LEGACY FALLBACK
        if (cgst > 0 || sgst > 0) {
          const expectedExclusiveTotal = discountedTotalBase + cgst + sgst;
          if (Math.abs(tTotal - expectedExclusiveTotal) < 0.1) {
            taxable = discountedTotalBase;
            displayTotal = tTotal;
            displayRate = tTotal / Number(item.quantity || 1);
          } else {
            taxable = discountedTotalBase - (cgst + sgst);
            displayTotal = discountedTotalBase;
            displayRate = discountedRateBase;
          }
        }
      }

      uiTotalGst += (cgst + sgst);

      if (!hsnSummary[hsn]) hsnSummary[hsn] = { taxable: 0, cgst: 0, sgst: 0 };
      hsnSummary[hsn].taxable += taxable;
      hsnSummary[hsn].cgst += cgst;
      hsnSummary[hsn].sgst += sgst;

      // Calculate Visual values to reflect the proportional discount directly on items
      const visualRate = displayRate;
      const visualTotal = displayTotal;

      return {
        ...item,
        rate: displayRate,
        total: displayTotal,
        visualRate,
        visualTotal,
        cgst,
        sgst,
        hsn
      };
    });

    // FIX: Sum the cleanly processed items so we don't double-subtract the discount!
    const netPayable = isSale
      ? discountedItems.reduce((sum, t) => sum + t.total, 0)
      : group.reduce((sum: number, t: any) => sum + (Number(t.total) || 0), 0);

    return { isSale, isGst, orderId, discountAmount, invoiceGrossTotal, netPayable, uiTotalGst, discountedItems, hsnSummary };
  };
  // ---------------------------------

  // Pre-fetch the short URL as soon as the transaction details modal opens
  useEffect(() => {
    if (!actionTransactionGroup) {
      setShortUrl(null);
      return;
    }
    const first = actionTransactionGroup[0];
    if (userMode === "pro" && first.type === "sale") {
      const orderId = first.fullOrderId || first.id.split("_")[0];
      publishInvoice(orderId)
        .then(url => { if (url) setShortUrl(url); })
        .catch(() => { });
    }
  }, [actionTransactionGroup, userMode]);

  const handlePrintRequest = async (group: any[]) => {
    const btDevice = getCachedBTPrinter(user?.uid);
    const usbDevice = getCachedUSBPrinter(user?.uid);
    if (btDevice || usbDevice) await executeDirectPrint(group, btDevice ? 'BT' : 'USB');
    else setShowPrinterConnect(true);
  };

  const executeDirectPrint = async (group: any[], type: 'BT' | 'USB') => {
    if (!user?.uid) return;
    try {
      const { orderId, isGst, discountAmount, invoiceGrossTotal, netPayable, discountedItems, hsnSummary } = processGroup(group);

      let finalUrl = shortUrl;
      if (userMode === "pro" && !finalUrl) {
        finalUrl = await publishInvoice(orderId).catch(() => null) || null;
        if (finalUrl) setShortUrl(finalUrl);
      }
      const fallbackUrl = `https://dailyhisaab.com/invoice/${user?.uid}/${orderId}`;

      const buildOpts = {
        layout: invoiceLayout || 'standard',
        businessName: businessProfile?.businessName || "RETAIL STORE",
        businessAddress: businessProfile?.businessAddress,
        businessPhone: businessProfile?.businessPhone,
        businessEmail: businessProfile?.businessEmail,
        businessWebsite: businessProfile?.businessWebsite,
        businessLogo: businessLogo || undefined,
        gstin: businessProfile?.gstin,
        cin: businessProfile?.cin,
        fssai: businessProfile?.fssai,
        invoiceDate: new Date(group[0].createdAt),
        orderId,
        isGstBill: isGst,
        paperWidth: printerPaperWidth,
        printLogo: printerPrintLogo,
        printQrCode: printQrCode,
        invoiceUrl: finalUrl || fallbackUrl,
        invoiceFooter,
        invoiceTerms,
        items: discountedItems.map(item => ({
          name: item.productName || "ITEM",
          qty: item.quantity || 1,
          rate: item.visualRate || 0,
          total: item.visualTotal || 0,
          hsn: item.hsn || "",
          cgst: item.cgst || 0,
          sgst: item.sgst || 0,
        })),
        discount: discountAmount,
        invoiceTotal: invoiceGrossTotal,
        netPayable,
        hsnSummary: isGst ? hsnSummary : undefined,
        businessVerified
      };

      const escposData = await buildReceiptEscPos(buildOpts as any);

      if (type === 'BT') {
        setBtStatus("connecting");
        try {
          await sendToBTPrinter(user.uid, escposData);
          setBtStatus("success");
        } catch (err: any) {
          if (err.message.includes("connected") || err.message.includes("pair")) {
            await connectBTPrinter(user.uid);
            await sendToBTPrinter(user.uid, escposData);
            setBtStatus("success");
          } else throw err;
        }
      } else {
        setUsbStatus("connecting");
        const cached = getCachedUSBPrinter(user.uid);
        let device: any; let endpoint: number;

        if (cached) {
          device = cached.device; endpoint = cached.endpoint;
        } else {
          const conn = await connectUSBPrinter(user.uid);
          device = conn.device; endpoint = conn.endpoint;
        }
        await sendToUSBPrinter(device, endpoint, escposData, user.uid);
        setUsbStatus("success");
      }

      // Delay closing modal so user can see "Done!" status
      setTimeout(() => {
        setActionTransactionGroup(null);
        setBtStatus("idle");
        setUsbStatus("idle");
      }, 1500);

    } catch (err: any) {
      setBtStatus("idle");
      setUsbStatus("idle");
      toast({ title: "Print Failed", description: err.message, variant: "destructive" });
    }
  };

  const handleWhatsAppAction = async (group: any[]) => {
    if (!showPhoneInput) { setShowPhoneInput(true); return; }
    const cleanNumber = phoneNumber.replace(/\D/g, "");
    if (cleanNumber.length < 10) return;

    const { orderId, discountAmount, netPayable, uiTotalGst, discountedItems } = processGroup(group);

    const itemsList = discountedItems.map(item => {
      return `• ${item.productName}: ${item.quantity} x ₹${Number(item.visualRate).toFixed(2)} = ₹${Number(item.visualTotal).toFixed(2)}`;
    }).join("\n");

    const bName = businessProfile?.businessName || "Retail Store";
    const gstText = uiTotalGst > 0 ? `\nGST Amount: ₹${uiTotalGst.toFixed(2)}` : "";
    const discountText = discountAmount > 0 ? `\nSavings: ₹${discountAmount.toFixed(2)}` : "";

    let invoiceLinkText = "";
    if (userMode === "pro") {
      let finalUrl = shortUrl;
      const fallbackUrl = `https://dailyhisaab.com/invoice/${user?.uid}/${orderId}`;
      if (!finalUrl) {
        finalUrl = await publishInvoice(orderId).catch(() => null) || null;
        if (finalUrl) setShortUrl(finalUrl);
      }
      invoiceLinkText = `\n\nView Invoice: ${finalUrl || fallbackUrl}`;
    }

    // keep in sync with the DAILYHISAAB VERIFIED badge
    const verifiedText = businessVerified ? "**** DAILYHISAAB VERIFIED ****\n" : "";

    const message = `*${bName.toUpperCase()}*\n\n*INVOICE*\nDate: ${new Date(group[0].createdAt).toLocaleDateString()}\n--------------------------\n${itemsList}\n--------------------------${gstText}${discountText}\n*GRAND TOTAL: ₹${netPayable.toFixed(2)}*${invoiceLinkText}\n\n${verifiedText}_Generated by DailyHisaab_`;

    window.open(`https://wa.me/91${cleanNumber}?text=${encodeURIComponent(message)}`, "_blank");
    if (spreadsheetId) saveCustomerPhone(spreadsheetId, cleanNumber).catch(() => { });
    setShowPhoneInput(false); setPhoneNumber(""); setActionTransactionGroup(null);
  };

  const handleConvertToGst = async (group: any[]) => {
    const ids = group.map((t: any) => t.id);
    try {
      const newOrderId = await convertToGstBill(ids);
      if (newOrderId) toast({ title: "Converted to GST Bill", description: `Invoice: ${newOrderId}` });
    } catch (err: any) {
      toast({ title: "Failed to convert", description: err.message, variant: "destructive" });
    } finally { setGstConfirmGroup(null); }
  };

  const executeDelete = async () => {
    if (!deleteConfirmGroup) return;
    try {
      // 1. Grab the order ID BEFORE we start deleting individual items
      const orderId = deleteConfirmGroup[0].fullOrderId || deleteConfirmGroup[0].id.replace(/_\d+$/, "");

      // GST bills are soft-deleted (cancellation preserves INV serial). For those we keep the
      // public-invoice doc alive so the share link still resolves with a "cancelled" banner.
      const isGstBill = deleteConfirmGroup.some((t: any) => t.isGstBill);

      // 2. Delete all items concurrently
      await Promise.all(deleteConfirmGroup.map((t: any) => deleteTransaction(t.id)));

      // 3. Force the public invoice to delete immediately, bypassing the race condition (non-GST only)
      if (user && orderId && !isGstBill) {
        await deletePublicInvoice(user.uid, orderId);
      }

      toast({ title: isGstBill ? "Invoice Cancelled" : "Transaction Deleted" });
      setActionTransactionGroup(null);
    } catch (error: any) {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    } finally { setDeleteConfirmGroup(null); }
  };

  const groupedTransactions = useMemo(() => {
    const groups = new Map<string, typeof transactions>();
    const sorted = [...transactions].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    sorted.forEach((t) => {
      let baseId = (t as any).fullOrderId || t.id;
      if (baseId.includes("_")) baseId = baseId.split("_").slice(0, -1).join("_");
      if (!groups.has(baseId)) groups.set(baseId, []);
      groups.get(baseId)!.push(t);
    });
    return Array.from(groups.values()).filter(group => {
      if (typeFilter !== "all" && group[0].type !== typeFilter) return false;
      if (dateFilter !== "all") {
        const d = new Date(group[0].createdAt);
        const now = new Date();

        if (dateFilter === "today") {
          return d >= new Date(new Date().setHours(0, 0, 0, 0));
        } else if (dateFilter === "week") {
          const sevenDaysAgo = new Date();
          sevenDaysAgo.setDate(now.getDate() - 7);
          return d >= sevenDaysAgo;
        } else if (dateFilter === "month") {
          return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
        } else if (dateFilter === "year") {
          return d.getFullYear() === now.getFullYear();
        }
      }
      return true;
    });
  }, [transactions, typeFilter, dateFilter]);

  const displayedGroups = groupedTransactions.slice(0, pageSize);

  return (
    <div className="flex flex-col min-h-screen bg-[#FFFBF2] dark:bg-[#1A1614] text-left">
      <header className="sticky top-0 z-30 bg-[#FFFBF2]/90 dark:bg-[#1A1614]/90 backdrop-blur-md px-4 py-3 flex items-center justify-between">
        <Logo size="md" />
        <ReportDialog transactions={transactions} products={[]} />
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-4 space-y-4 pb-24">
        {/* Dashboard Block */}
        <div className="bg-white dark:bg-slate-900 rounded-3xl p-5 shadow-sm border border-slate-100 dark:border-slate-800">
          <div className="mb-4 pb-4 border-b border-slate-100 dark:border-slate-800 cursor-pointer" onClick={() => setShowTodaySales(true)}>
            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest flex items-center justify-between">
              <span>Today's Revenue</span> <ChevronRight className="h-3 w-3" />
            </p>
            <p className="text-4xl font-black text-slate-900 dark:text-white">₹{(dashboardStats?.todaySales || 0).toFixed(2)}</p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Monthly Sales</p><p className="text-lg font-extrabold text-emerald-600">₹{(dashboardStats?.monthSales || 0).toFixed(2)}</p></div>
            <div><p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1">Total Expenses</p><p className="text-lg font-extrabold text-red-600">₹{Math.abs(dashboardStats?.monthExpenses || 0).toFixed(2)}</p></div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2">
          <Select value={typeFilter} onValueChange={(v) => setTypeFilter(v as any)}>
            <SelectTrigger className="flex-1 bg-white dark:bg-slate-900 rounded-2xl h-10 shadow-sm border-slate-100"><Filter className="h-3 w-3 mr-1.5" /><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Types</SelectItem><SelectItem value="sale">Sales</SelectItem><SelectItem value="expense">Expenses</SelectItem></SelectContent>
          </Select>
          <Select value={dateFilter} onValueChange={(v) => setDateFilter(v as any)}>
            <SelectTrigger className="flex-1 bg-white dark:bg-slate-900 rounded-2xl h-10 shadow-sm border-slate-100"><SelectValue placeholder="Date" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Time</SelectItem>
              <SelectItem value="today">Today</SelectItem>
              <SelectItem value="week">Last 7 Days</SelectItem>
              <SelectItem value="month">This Month</SelectItem>
              <SelectItem value="year">This Year</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <h3 className="font-extrabold text-slate-900 dark:text-white text-lg tracking-tight">Recent Ledger</h3>

        {/* List */}
        {isLoading ? <TransactionItemSkeleton /> : displayedGroups.length > 0 ? (
          <div className="bg-white dark:bg-slate-900 rounded-3xl shadow-sm border border-slate-100 dark:border-slate-800 overflow-hidden divide-y divide-slate-50 dark:divide-slate-800/50">
            {displayedGroups.map((group) => {
              const first = group[0];
              const { isSale, isGst, netPayable } = processGroup(group);
              const itemsText = group.filter(t => !t.productName.toLowerCase().includes("discount")).map(t => `${t.quantity}x ${t.productName}`).join(", ");
              const isCancelled = group.some((t: any) => t.cancelled);

              return (
                <div key={first.id} className={`flex items-center justify-between p-4 cursor-pointer active:bg-slate-50 dark:active:bg-slate-800 transition-colors ${isCancelled ? 'opacity-60' : ''}`} onClick={() => { setShowPhoneInput(false); setActionTransactionGroup(group); }}>
                  <div className="flex items-center gap-3 min-w-0 pr-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shrink-0 ${isCancelled ? 'bg-slate-100 text-slate-400' : (isSale ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600')}`}>
                      {isSale ? <ArrowUpRight className="h-5 w-5" /> : <ArrowDownRight className="h-5 w-5" />}
                    </div>
                    <div className="min-w-0">
                      <p className={`font-bold text-sm text-slate-900 dark:text-white truncate ${isCancelled ? 'line-through' : ''}`}>{itemsText || (isSale ? 'Sale' : 'Expense')}</p>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <p className="text-[11px] font-medium text-slate-400">{new Date(first.createdAt).toLocaleDateString("en-IN", { day: 'numeric', month: 'short' })}</p>
                        {isGst && <span className="bg-amber-50 text-amber-700 px-1.5 py-0.5 rounded text-[9px] font-black uppercase">GST</span>}
                        {isCancelled && <span className="bg-rose-50 text-rose-700 px-1.5 py-0.5 rounded text-[9px] font-black uppercase">Cancelled</span>}
                      </div>
                    </div>
                  </div>
                  <p className={`font-black text-sm ${isCancelled ? 'text-slate-400 line-through' : (isSale ? 'text-emerald-600' : 'text-slate-900 dark:text-white')}`}>{isSale ? '+' : '-'}₹{Math.abs(netPayable).toFixed(0)}</p>
                </div>
              );
            })}
          </div>
        ) : <EmptyState icon={IndianRupee} title="No transactions" description="Record your first transaction to appear here" />}

        {/* Pagination */}
        <div className="flex gap-2 justify-center pt-2">
          {[25, 50, 100].map(size => (
            <button key={size} onClick={() => setPageSize(size)} className={`px-4 py-2 rounded-2xl text-sm font-bold shadow-sm ${pageSize === size ? "bg-[#8B1A1A] text-white" : "bg-white text-slate-500 border"}`}>{size}</button>
          ))}
        </div>
      </div>

      {/* Action Sheet */}
      <Dialog open={!!actionTransactionGroup} onOpenChange={(open) => !open && setActionTransactionGroup(null)}>
        <DialogContent className="sm:max-w-[420px] rounded-[28px] border-none p-0 overflow-hidden shadow-2xl bg-[#FFFBEB] dark:bg-slate-900 mt-auto mb-0 sm:m-auto">
          {actionTransactionGroup && (() => {
            const first = actionTransactionGroup[0];
            const { isSale, isGst, netPayable, discountAmount, discountedItems } = processGroup(actionTransactionGroup);

            return (
              <>
                <div className="p-6 text-center">
                  <div className={`w-14 h-14 mx-auto rounded-full mb-3 flex items-center justify-center ${isSale ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                    {isSale ? <ArrowUpRight className="h-7 w-7" /> : <ArrowDownRight className="h-7 w-7" />}
                  </div>
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">{isSale ? 'TOTAL SALE' : 'TOTAL EXPENSE'}</p>
                  <h2 className={`text-4xl font-black mb-1 tracking-tight ${isSale ? 'text-emerald-600' : 'text-red-600'}`}>₹{Math.abs(netPayable).toFixed(2)}</h2>
                  <div className="flex justify-center items-center gap-2">
                    <p className="text-xs font-bold text-slate-500">{new Date(first.createdAt).toLocaleString()}</p>
                    {isGst && <span className="bg-amber-100 text-amber-700 px-2 py-0.5 rounded text-[10px] font-black">GST BILL</span>}
                  </div>
                </div>

                <div className="px-6 py-4 border-t border-amber-100 dark:border-slate-800 text-left">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-4">ITEMS</p>
                  <div className="space-y-3 max-h-[200px] overflow-y-auto pr-1">
                    {discountedItems.map((item, idx) => (
                      <div key={idx} className="flex justify-between items-center text-sm">
                        <span className="text-[#475569] dark:text-slate-400 font-medium">{item.quantity}x {item.productName}</span>
                        <span className="font-bold text-[#0F172A] dark:text-white">₹{Number(item.visualTotal).toFixed(2)}</span>
                      </div>
                    ))}
                    {discountAmount > 0 && (
                      <div className="flex justify-between items-center text-sm pt-2 border-t border-amber-50 dark:border-slate-800">
                        <span className="text-emerald-600 font-bold">Discount Applied</span>
                        <span className="text-emerald-600 font-bold">-₹{discountAmount.toFixed(2)}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="p-4 space-y-2 border-t border-amber-100 dark:border-slate-800">
                  <AnimatePresence mode="wait">
                    {!showPhoneInput ? (
                      <motion.div key="main-actions" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -10 }} className="space-y-2">
                        {isSale && (
                          <>
                            <button
                              onClick={() => handlePrintRequest(actionTransactionGroup)}
                              disabled={btStatus !== "idle" || usbStatus !== "idle"}
                              className="w-full flex items-center gap-3 p-4 rounded-2xl bg-blue-50 dark:bg-blue-900/10 active:bg-blue-100 transition-all shadow-sm disabled:opacity-70 disabled:cursor-not-allowed"
                            >
                              <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600">
                                {(btStatus === "connecting" || btStatus === "printing" || usbStatus === "connecting" || usbStatus === "printing") ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (btStatus === "success" || usbStatus === "success") ? (
                                  <Check className="h-4 w-4 text-green-600" />
                                ) : (
                                  <Printer className="h-4 w-4" />
                                )}
                              </div>
                              <span className="font-bold text-blue-700 dark:text-blue-400">
                                {btStatus === "connecting" || usbStatus === "connecting" ? "Connecting..."
                                  : btStatus === "printing" || usbStatus === "printing" ? "Printing..."
                                    : btStatus === "success" || usbStatus === "success" ? "Print Successful!"
                                      : "Print Invoice"}
                              </span>
                            </button>
                            <button onClick={() => handleWhatsAppAction(actionTransactionGroup)} className="w-full flex items-center gap-3 p-4 rounded-2xl bg-emerald-50 dark:bg-emerald-900/10 active:bg-emerald-100 transition-all shadow-sm">
                              <div className="w-8 h-8 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center text-emerald-600"><MessageSquare className="h-4 w-4" /></div>
                              <span className="font-bold text-emerald-700 dark:text-emerald-400">Share via WhatsApp</span>
                            </button>
                            {userMode === "pro" && !isGst && (
                              <button onClick={() => { setActionTransactionGroup(null); setGstConfirmGroup(actionTransactionGroup); }} className="w-full flex items-center gap-3 p-4 rounded-2xl bg-amber-50 dark:bg-amber-900/10 active:bg-amber-100 transition-all shadow-sm">
                                <div className="w-8 h-8 rounded-full bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center text-amber-600"><Receipt className="h-4 w-4" /></div>
                                <span className="font-bold text-amber-700 dark:text-amber-400">Convert to GST Bill</span>
                              </button>
                            )}
                          </>
                        )}
                        <button onClick={() => { setActionTransactionGroup(null); setDeleteConfirmGroup(actionTransactionGroup); }} className="w-full flex items-center gap-3 p-4 rounded-2xl bg-red-50 dark:bg-red-900/10 active:bg-red-100 transition-all shadow-sm">
                          <div className="w-8 h-8 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-red-600"><Trash2 className="h-4 w-4" /></div>
                          <span className="font-bold text-red-600 dark:text-red-400">Delete Transaction</span>
                        </button>
                      </motion.div>
                    ) : (
                      <motion.div key="phone-input" initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="p-2 space-y-4">
                        <div className="flex gap-2">
                          <Input type="tel" placeholder="Customer 10-digit number" value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)} className="h-14 rounded-2xl border-2 border-[#22C55E] bg-white text-lg focus-visible:ring-0" autoFocus maxLength={10} />
                          <Button onClick={() => handleWhatsAppAction(actionTransactionGroup)} disabled={phoneNumber.length < 10} className="h-14 w-14 rounded-2xl bg-[#22C55E] hover:bg-[#1ea34d] p-0 flex-shrink-0 transition-transform active:scale-90"><Send className="h-6 w-6 text-white" /></Button>
                        </div>
                        <Button variant="ghost" onClick={() => setShowPhoneInput(false)} className="w-full h-12 rounded-xl text-slate-500 font-bold">Back to Actions</Button>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* Printer Connect Modal */}
      <Dialog open={showPrinterConnect} onOpenChange={setShowPrinterConnect}>
        <DialogContent className="max-w-sm rounded-[2rem] p-6 bg-white dark:bg-slate-900 border-none shadow-2xl">
          <DialogTitle className="text-xl font-bold text-center mb-2">No Printer Connected</DialogTitle>
          <p className="text-sm text-slate-500 text-center mb-6">Select a connection method to print this invoice.</p>
          <div className="space-y-3 flex flex-col">
            <Button
              onClick={async () => {
                try {
                  setShowPrinterConnect(false);
                  await connectBTPrinter(user?.uid!);
                  if (actionTransactionGroup) executeDirectPrint(actionTransactionGroup, 'BT');
                } catch (e: any) {
                  toast({ title: "BT Error", description: e.message, variant: "destructive" });
                }
              }}
              className="w-full h-14 text-white font-bold text-lg transition-all active:scale-[0.97]"
              style={{
                borderRadius: "9999px",
                background: "linear-gradient(135deg, #B91C1C 0%, #962222 100%)",
                boxShadow: "0 4px 14px rgba(185,28,28,0.35), 0 1px 3px rgba(0,0,0,0.1)",
              }}
            >
              <Bluetooth className="h-5 w-5 mr-2" /> Bluetooth Printer
            </Button>
            <Button
              onClick={async () => {
                try {
                  setShowPrinterConnect(false);
                  await connectUSBPrinter(user?.uid!);
                  if (actionTransactionGroup) executeDirectPrint(actionTransactionGroup, 'USB');
                } catch (e: any) {
                  toast({ title: "USB Error", description: e.message, variant: "destructive" });
                }
              }}
              variant="outline"
              className="w-full h-14 font-bold text-base transition-all active:scale-[0.97] border-2"
              style={{
                borderRadius: "9999px",
                background: "transparent",
                borderColor: "#B91C1C",
                color: "#B91C1C",
              }}
            >
              <Usb className="h-5 w-5 mr-2" /> USB Printer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Confirmation Dialogs */}
      <AlertDialog open={!!gstConfirmGroup} onOpenChange={(o) => !o && setGstConfirmGroup(null)}>
        <AlertDialogContent className="rounded-[2rem] border-none shadow-2xl max-w-sm">
          <div className="p-8 pb-6 text-center">
            <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-6"><Receipt className="h-8 w-8 text-amber-600" /></div>
            <AlertDialogTitle className="text-2xl font-bold text-amber-700 tracking-tight">Convert to GST?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500 text-[15px] leading-relaxed mt-2">Generate permanent GST invoice. Cannot be undone.</AlertDialogDescription>
          </div>
          <div className="px-6 pb-6 flex flex-col gap-3">
            <AlertDialogAction className="w-full py-4 rounded-xl font-semibold text-white bg-amber-600" onClick={() => gstConfirmGroup && handleConvertToGst(gstConfirmGroup)}>Yes, Convert</AlertDialogAction>
            <AlertDialogCancel className="w-full py-4 rounded-xl font-semibold border-none bg-slate-100">Cancel</AlertDialogCancel>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!deleteConfirmGroup} onOpenChange={(o) => !o && setDeleteConfirmGroup(null)}>
        <AlertDialogContent className="rounded-[2rem] border-none shadow-2xl max-w-sm">
          <div className="p-8 pb-6 text-center">
            <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-6"><Trash2 className="h-8 w-8 text-red-600" /></div>
            <AlertDialogTitle className="text-2xl font-bold text-red-600 tracking-tight">Delete Transaction?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-500 text-[15px] mt-2">Records permanently deleted.</AlertDialogDescription>
          </div>
          <div className="px-6 pb-6 flex flex-col gap-3">
            <AlertDialogAction className="w-full py-4 rounded-xl font-semibold text-white bg-red-600" onClick={executeDelete}>Yes, Delete</AlertDialogAction>
            <AlertDialogCancel className="w-full py-4 rounded-xl font-semibold border-none bg-slate-100">Cancel</AlertDialogCancel>
          </div>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

type FilterType = "all" | "sale" | "expense";
type DateFilter = "today" | "week" | "month" | "year" | "all";